/**
 * @module TokenEventHandler
 * @description Simplified token event handling with cache invalidation.
 *
 * Provides cache invalidation utilities for token-related data.
 * Real-time token updates are handled directly by the PostgreSQL NOTIFY/LISTEN pattern.
 *
 * Effective cost remains nullable and is never coerced to zero.
 *
 * Listens for the `resync_required` event emitted by the backend `token_listener`
 * when the PostgreSQL NOTIFY listener reconnects; on receipt, invalidates the
 * TanStack Query `['forecasting']` and `['tokens']` caches so the UI re-fetches
 * fresh data.
 *
 * TanStack Query is the only response cache — every invalidation here goes
 * through the wired QueryClient, so a real-time event can never be defeated
 * by a second, service-level cache layer.
 *
 * Exports:
 * - invalidateTokenCache: Manually invalidate token cache
 * - initializeTokenEventHandling: Connects the shared socket and subscribes
 *   to resync_required/token_update; returns a cleanup that unsubscribes.
 */

import { webSocketService } from './simpleWebSocket'

// TanStack QueryClient — resolved lazily from the module-level import to avoid
// circular dependency at startup. Set via setQueryClientForTokenHandler.
let _queryClient: import('@tanstack/react-query').QueryClient | null = null;

/**
 * Wire the TanStack QueryClient so the resync_required handler can call
 * invalidateQueries. Called once from App.tsx (or wherever QueryClientProvider
 * is mounted) after the QueryClient is created.
 */
export const setQueryClientForTokenHandler = (
  qc: import('@tanstack/react-query').QueryClient
): void => {
  _queryClient = qc;
};

/**
 * Invalidate all token-related cache entries
 *
 * @param sessionId - Optional specific session to invalidate
 */
export const invalidateTokenCache = (sessionId?: string): void => {
  if (!_queryClient) {
    console.warn('tokenEventHandler: token cache invalidation requested but no QueryClient set')
    return
  }
  try {
    // Invalidate general token queries (analytics, additional metrics, …)
    _queryClient.invalidateQueries({ queryKey: ['tokens'] })

    // Invalidate session-scoped analytics if a session is identified
    if (sessionId) {
      _queryClient.invalidateQueries({ queryKey: ['analytics', 'session', sessionId] })
    }

    console.debug('Token cache invalidated', { sessionId })
  } catch (error) {
    console.error('Error invalidating token cache:', error)
  }
}

/**
 * Handle the resync_required event emitted by the backend token_listener
 * when it reconnects to the PostgreSQL NOTIFY channel. Invalidates the
 * TanStack Query forecasting and token caches so the UI re-fetches fresh data.
 *
 * A nullable effective cost must remain unknown when the cache is refreshed.
 */
export const handleResyncRequired = (): void => {
  if (!_queryClient) {
    console.warn('tokenEventHandler: resync_required received but no QueryClient set')
    return;
  }
  _queryClient.invalidateQueries({ queryKey: ['forecasting'] });
  _queryClient.invalidateQueries({ queryKey: ['tokens'] });
  console.debug('tokenEventHandler: resync_required — invalidated forecasting + tokens queries');
}

/**
 * Initialize global token event handling.
 *
 * Registers handlers on the shared webSocketService:
 *   - `resync_required` → invalidate forecasting + tokens caches
 *   - `token_update`    → invalidate the per-session analytics cache so
 *                         components driven by useSessionAnalytics refresh
 *                         without polling.
 * Returns a cleanup function that removes the listeners.
 */
export const initializeTokenEventHandling = (): (() => void) => {
  console.info('Token event handling initialized')

  let unmounted = false;
  const unsubscribers: Array<() => void> = [];

  webSocketService.connect()
    .then(() => {
      if (unmounted) return;
      unsubscribers.push(
        webSocketService.on('resync_required', () => handleResyncRequired())
      );
      unsubscribers.push(
        webSocketService.on('token_update', (event: unknown) => {
          if (!_queryClient) return;
          const sessionId = (event as { session_id?: string } | null)?.session_id;
          if (!sessionId) return;
          _queryClient.invalidateQueries({ queryKey: ['analytics', 'session', sessionId] });
        })
      );
    })
    .catch(() => {
      // Connection failed (test env, server down) — silent.
    });

  return () => {
    unmounted = true;
    while (unsubscribers.length > 0) {
      const fn = unsubscribers.pop();
      try { fn?.(); } catch { /* ignore */ }
    }
    console.info('Token event handlers cleaned up')
  }
}

/**
 * Check if token event handling is active (compatibility function)
 */
export const isTokenEventHandlingActive = (): boolean => {
  // Always return true since the new system is always active
  return true
}
